import { ValveScheduleCreate } from '@data/device/model/valveSchedule'

export type ValveScheduleBatchOutcome =
  | {
    status: 'success'
    title: string
    message: string
  }
  | {
    status: 'failure'
    title: string
    message: string
    failed: ValveScheduleCreate[]
  }

export const describeValveSchedule = (schedule: ValveScheduleCreate) =>
  `${schedule.valveId} ${schedule.dayOfWeek} ${schedule.timeOfDay.slice(0, 5)}`

// `results` must be the Promise.allSettled output for `schedules`, in the same order —
// allSettled preserves input order, so each result is labelled by its index.
export const getValveScheduleBatchOutcome = (
  schedules: ValveScheduleCreate[],
  results: PromiseSettledResult<unknown>[],
  getErrorMessage: (error: unknown) => string | undefined,
): ValveScheduleBatchOutcome => {
  const failures = results.flatMap((result, index) =>
    result.status === 'rejected'
      ? [{
        schedule: schedules[index],
        reason: getErrorMessage(result.reason) ?? 'Unknown error',
      }]
      : [],
  )

  if (failures.length === 0) {
    return {
      status: 'success',
      title: 'Schedules created',
      message: schedules.length === 1
        ? 'Created 1 valve schedule.'
        : `Created ${schedules.length} valve schedules.`,
    }
  }

  const created = schedules.length - failures.length
  const failedList = failures
    .map(({ schedule, reason }) => `${describeValveSchedule(schedule)} (${reason})`)
    .join('; ')

  return {
    status: 'failure',
    title: `${failures.length} of ${schedules.length} schedules failed`,
    message: created > 0
      ? `Failed: ${failedList}. The other ${created} were saved; saving again retries only the failed schedules.`
      : `Failed: ${failedList}.`,
    failed: failures.map(({ schedule }) => schedule),
  }
}
